import { GridRowSelectionModel } from "@mui/x-data-grid";
import {
    Fab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemText,
    Typography,
} from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import { useUpdateRecords } from "../../../../api/useUpdateRecords";
import { useStore } from "../../../../store/store";
import { useState } from "react";
import { format, parseISO } from "date-fns";
import { pt } from "date-fns/locale";
import { CancelledNotesDialog } from "../CancelledNotesDialog";

type CancelSelectedSessionsProps = {
    selectedRows: Array<number>;
    setSelectedRows: (rows: GridRowSelectionModel) => void;
    items: Array<any>;
};

export const CancelSelectedSessions = ({ selectedRows, setSelectedRows, items }: CancelSelectedSessionsProps) => {
    const updateSessions = useUpdateRecords({ queryInvalidatePrefix: "sessions-items" });
    const { notifications } = useStore();
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [isNotesDialogOpen, setIsNotesDialogOpen] = useState(false);

    const sessionsToCancel = items.filter((item) => selectedRows.includes(item.id));

    const handleOpenConfirmDialog = () => {
        setIsConfirmDialogOpen(true);
    };

    const handleCloseConfirmDialog = () => {
        setIsConfirmDialogOpen(false);
    };

    const handleProceedToNotes = () => {
        setIsConfirmDialogOpen(false);
        setIsNotesDialogOpen(true);
    };

    const handleNotesCancel = () => {
        setIsNotesDialogOpen(false);
    };

    const handleNotesConfirm = async (notes: string) => {
        const validSessionIds = selectedRows.filter((id): id is number => typeof id === "number" && !isNaN(id) && id > 0);

        if (validSessionIds.length === 0) {
            setIsNotesDialogOpen(false);
            notifications.setNotification({
                isOpen: true,
                message: "Nenhuma sessão válida selecionada",
                severity: "warning",
            });
            return;
        }

        try {
            await updateSessions.mutateAsync({
                entity: "sessions",
                data: validSessionIds.map((id) => ({ id, status: "staff_unavailable", notes })),
            });
            notifications.setNotification({
                isOpen: true,
                message: "Sessões canceladas com sucesso",
                severity: "success",
            });
            setSelectedRows([]);
            setIsNotesDialogOpen(false);
        } catch (error: any) {
            notifications.setNotification({
                isOpen: true,
                message: error.response?.data?.errors?.[0]?.message || "Erro ao cancelar sessões",
                severity: "error",
            });
        }
    };

    const formatSessionDate = (dateString: string) => {
        const date = parseISO(dateString);
        const formattedDate = format(date, "EEEE, dd/MM/yyyy 'às' HH:mm", { locale: pt });
        return formattedDate;
    };

    if (selectedRows.length === 0) return null;

    return (
        <>
            <Fab
                color="warning"
                size="medium"
                aria-label="cancel selected sessions"
                onClick={handleOpenConfirmDialog}
                sx={{ boxShadow: 3 }}
            >
                <BlockIcon />
            </Fab>
            <Dialog open={isConfirmDialogOpen} onClose={handleCloseConfirmDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Cancelar {selectedRows.length} {selectedRows.length === 1 ? "sessão" : "sessões"}?
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        As seguintes sessões serão marcadas como canceladas:
                    </Typography>
                    <List dense sx={{ maxHeight: 300, overflow: "auto" }}>
                        {sessionsToCancel.map((session) => (
                            <ListItem key={session.id} sx={{ py: 0.5 }}>
                                <ListItemText
                                    primary={session.service?.name || "Sem serviço"}
                                    secondary={formatSessionDate(session.start_date)}
                                />
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirmDialog}>Voltar</Button>
                    <Button onClick={handleProceedToNotes} color="warning" variant="contained">
                        Cancelar Sessões
                    </Button>
                </DialogActions>
            </Dialog>
            <CancelledNotesDialog
                isOpen={isNotesDialogOpen}
                onConfirm={handleNotesConfirm}
                onCancel={handleNotesCancel}
            />
        </>
    );
};
